import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Grid, Link, Tooltip } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormProps } from 'util/Form';
import { ApplicationQuestionOption, SublistQuestion } from 'store/types/ApplicationQuestion';
import { TableColumn } from 'store/types/Table';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import Table from 'components/shared/Table';
import Add from '@mui/icons-material/Add';
import { defaultGridContainerProps, defaultGridItemProps, defaultSnackbarErrorProps } from 'util/Layout';
import AddSublistItemModal from './AddSublistItemModal';
import QuestionType from 'store/enums/QuestionType';
import FileInfo from 'store/types/FileInfo';
import { useSnackbar } from 'notistack';
import classNames from 'classnames';
import { getFormattedDate, NUMBER_DATE_FORMAT, truncateFilename } from 'util/Format';
import { getPrice } from 'util/Payment';
import { SublistAnswer } from 'store/types/ApplicationAnswer';
import ApplicationsService from 'services/api/ApplicationsService';

import commonStyles from 'styles/common.module.scss';
import { useParams } from 'react-router';

interface SublistQuestionFormItemProps extends FormProps {
  id: string;
  fieldName: string;
  applicationId?: string;
  sublistQuestions?: SublistQuestion[];
}

interface SublistAnswerView extends SublistAnswer {
  type: QuestionType;
  content: string;
  result?: string;
  files?: FileInfo[];
  subtotal?: number;
}

interface SublistAnswerGroupView {
  referenceKey: string;
  answers: SublistAnswerView[];
  isSubtotal?: boolean;
}

const SublistQuestionFormItem: React.FunctionComponent<SublistQuestionFormItemProps> = ({
  id: parentId,
  fieldName,
  applicationId = '',
  sublistQuestions,
  disabled,
}) => {
  const { control, getValues, setValue } = useFormContext();
  const { enqueueSnackbar } = useSnackbar();
  const { applicationId: id = applicationId } = useParams<{ applicationId: string }>();
  const [list, setList] = useState<SublistAnswerGroupView[]>([]);
  const [loading, setLoading] = useState<boolean>();
  const [displayResultRow, setDisplayResultRow] = useState<boolean>(false);
  const [addItemModalOpen, setAddItemModalOpen] = useState<boolean>(false);
  const sublistAnswersView: Array<SublistAnswerView[]> = useWatch({ control, name: fieldName });
  const dropdownQuestionOptions: ApplicationQuestionOption[] | undefined = useMemo(
    () => sublistQuestions?.find((item) => item.type === QuestionType.Dropdown)?.options,
    [sublistQuestions]
  );

  useEffect(() => {
    setDisplayResultRow(
      !!sublistQuestions?.some(({ type }) => type === QuestionType.Currency && sublistAnswersView.length)
    );
  }, [sublistAnswersView, sublistQuestions]);

  useEffect(() => {
    const dropdownOptionsByContent = new Map<string, ApplicationQuestionOption[] | undefined>();
    sublistQuestions?.forEach((question) => {
      if (question.type === QuestionType.Dropdown) dropdownOptionsByContent.set(question.content, question.options);
    });

    const formatAnswer = (sublistAnswer: SublistAnswerView) => {
      const { type, content, answer } = sublistAnswer;

      switch (type) {
        case QuestionType.Dropdown:
          return dropdownOptionsByContent.get(content)?.find((o) => o.value === answer)?.name || '';
        case QuestionType.Date:
          return getFormattedDate(answer, NUMBER_DATE_FORMAT);
        default:
          return answer;
      }
    };

    const groupedAnswers = new Map<string, SublistAnswerGroupView>();

    sublistAnswersView.forEach((row) =>
      row.forEach((sublistAnswer) => {
        const { referenceKey } = sublistAnswer;
        const view: SublistAnswerView = { ...sublistAnswer, answer: formatAnswer(sublistAnswer) };

        const existing = groupedAnswers.get(referenceKey);
        if (existing) existing.answers.push(view);
        else groupedAnswers.set(referenceKey, { referenceKey, answers: [view] });
      })
    );

    const updatedList = Array.from(groupedAnswers.values());

    const subtotals: SublistAnswerGroupView = { referenceKey: '', answers: [], isSubtotal: true };

    updatedList.forEach((item) =>
      item.answers.forEach((answer) => {
        if (answer.type === QuestionType.Currency) {
          const currentSubtotal = subtotals.answers.find((sub) => sub.content === answer.content);
          if (currentSubtotal) currentSubtotal.subtotal = (currentSubtotal.subtotal || 0) + Number(answer.answer || 0);
          else
            subtotals.answers.push({
              ...answer,
              subtotal: Number(answer.answer || 0),
            });
        }
      })
    );

    if (subtotals.answers.length) updatedList.push(subtotals);

    setList(updatedList);
  }, [displayResultRow, dropdownQuestionOptions, sublistAnswersView, sublistQuestions]);

  const handleDeleteItem = useCallback(
    (referenceKey: string) => () => {
      const formValues = getValues();
      const newValues = formValues[fieldName].filter(
        (item: SublistAnswerView[]) => item[0].referenceKey !== referenceKey
      );
      setValue(fieldName, newValues);

      const sublistFiles = formValues['sublistFiles'];
      if (sublistFiles && sublistFiles[referenceKey]?.files) {
        sublistFiles[referenceKey].files = [];
      }
    },
    [fieldName, getValues, setValue]
  );

  const handleFileLinkClick = useCallback(
    (record: FileInfo) => () => {
      setLoading(true);
      ApplicationsService.getApplicationFile(id, record)
        .then(() => {
          setLoading(false);
        })
        .catch((error: string) => {
          enqueueSnackbar(error, defaultSnackbarErrorProps);
          setLoading(false);
        });
    },
    [id, enqueueSnackbar]
  );

  const columns: Array<TableColumn<any>> = useMemo(() => {
    const result: Array<TableColumn<any>> = sublistQuestions?.length
      ? sublistQuestions.map(({ content, type }) => ({
          label: content,
          key: content,
          dataIndex: content,
          render: (_: any, record: SublistAnswerGroupView) => {
            const sublistAnswer = record.answers.find((item) => item.content === content);

            switch (type) {
              case QuestionType.Currency:
                return record.isSubtotal
                  ? getPrice(Number(sublistAnswer?.subtotal || 0))
                  : getPrice(Number(sublistAnswer?.answer || 0));

              case QuestionType.File:
                return (
                  <>
                    {sublistAnswer?.files?.map((file) => {
                      const fileName = truncateFilename(file.name, 20, 'end');
                      return (
                        <Tooltip key={file.name} title={file.name} placement="top-start">
                          {file.id ? (
                            <div>
                              <Link onClick={handleFileLinkClick(file)} className={commonStyles.link}>
                                {fileName}
                              </Link>
                            </div>
                          ) : (
                            <div>{fileName}</div>
                          )}
                        </Tooltip>
                      );
                    })}
                  </>
                );

              default:
                return sublistAnswer?.answer;
            }
          },
        }))
      : [];

    result.push({
      label: 'Action',
      key: 'action',
      render: (_: any, record: SublistAnswerGroupView) => (
        <Button
          size={'small'}
          variant={'outlined'}
          className={classNames(commonStyles.dangerButtonOutlined, { [commonStyles.hidden]: !!record.isSubtotal })}
          onClick={handleDeleteItem(record.referenceKey)}
          disabled={disabled}
        >
          <DeleteOutlined />
        </Button>
      ),
    });
    {
      displayResultRow &&
        result.unshift({
          label: '',
          key: 'result',
          dataIndex: 'result',
          render: (_: any, record: SublistAnswerGroupView) => <b>{record.isSubtotal && 'Subtotal'}</b>,
        });
    }

    return result;
  }, [disabled, displayResultRow, handleDeleteItem, handleFileLinkClick, sublistQuestions]);

  const handleAddItemModalOpen = useCallback(() => {
    setAddItemModalOpen(true);
  }, []);

  const handleAddItemModalClose = useCallback(() => {
    setAddItemModalOpen(false);
  }, []);

  return (
    <>
      <Grid {...defaultGridContainerProps}>
        <Grid {...defaultGridItemProps} id={`${parentId}-alert`} data-alert={'true'} style={{ display: 'none' }}>
          <Alert severity="error" className={commonStyles.alert} style={{ marginBottom: 1 }}>
            Answer is required.
          </Alert>
        </Grid>
        <Grid {...defaultGridItemProps}>
          <Button
            color={'secondary'}
            variant={'contained'}
            startIcon={<Add />}
            className={commonStyles.uppercase}
            onClick={handleAddItemModalOpen}
            disabled={disabled}
          >
            {'New Item'}
          </Button>
        </Grid>
        <Grid {...defaultGridItemProps}>
          <Table columns={columns} list={list} loading={loading} />
        </Grid>
      </Grid>
      <AddSublistItemModal
        onClose={handleAddItemModalClose}
        open={addItemModalOpen}
        questions={sublistQuestions || []}
        fieldName={fieldName}
      />
    </>
  );
};
export default SublistQuestionFormItem;
